/**
 * useGridDrag — the pointer half of dragging an event.
 *
 * CAL-PRO-2. The arithmetic is in `timeGrid` where it can be tested;
 * this is the part that cannot be: a pointer state machine. It owns
 * the press, the threshold, the frame loop and the release, and it
 * knows nothing about the DOM — the view hands it a `resolve` that
 * turns a client point into a day and a minute, and gets back a span
 * to paint.
 *
 * Four decisions worth stating, because each one is a bug avoided:
 *
 * THE WINDOW IS THE EVENT SOURCE, NOT THE ELEMENT. Only the press is
 * a React handler; move, release and cancel are listeners on `window`
 * for the life of the drag. The first version listened on the chip
 * itself, which breaks the moment the chip goes away mid-drag — and
 * it does: a client booking arrives over the realtime channel, the
 * query refetches, the column re-clusters, and React unmounts the
 * element the pointer is holding. No element, no release event, and
 * the frame loop below would run until the tab closed. A listener on
 * the window cannot be unmounted out from under a gesture.
 *
 * CAPTURE STAYS ANYWAY. Not for the events — for the click. Pointer
 * capture is what makes the browser fire the trailing `click` on the
 * element the press started on however far the pointer travelled,
 * which is what `swallowClick` relies on.
 *
 * ONE FRAME, NOT ONE EVENT. `pointermove` fires faster than the
 * screen refreshes, so moves only record where the pointer is and a
 * rAF loop does the work. That throttles the repaint to the refresh
 * rate for free, and — the real reason — it lets the ghost follow the
 * grid when the grid SCROLLS under a held pointer, which a
 * move-driven implementation cannot do.
 *
 * A THRESHOLD, OR TAPS STOP WORKING. Nothing happens until the
 * pointer has travelled four pixels; under that it was a click, and
 * the click must still open the event. Once it has, the click that
 * follows the release is swallowed — the browser fires one regardless
 * of how far the pointer went.
 *
 * MOUSE AND PEN ONLY. A touch drag that moved events would have to
 * steal the vertical swipe that scrolls the grid, and on a phone
 * scrolling the day matters more than dragging within it. Touch
 * calendars arm a move with a long press instead; that is a gesture
 * of its own and not this change. Taps work on touch exactly as they
 * did.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { resolveCreate, resolveMove, resolveResize, type Span } from './timeGrid';

export type DragKind = 'move' | 'resize-start' | 'resize-end' | 'create';

/** A client point in the grid's own terms. */
export interface GridPoint {
  dayIndex: number;
  /** Minutes from the top of that column, clamped into the day. */
  minutes: number;
}

export interface DragPreview extends Span {
  kind: DragKind;
  /** Which column to paint in. Not always the span's own day: a
   *  create that runs past midnight still belongs to the column it
   *  was drawn in. */
  dayIndex: number;
}

export interface DragCommit extends Span {
  kind: DragKind;
  /** The event being moved or resized. Absent for a create. */
  id?: string;
}

/** Under this, it was a click. */
const THRESHOLD_PX = 4;

export interface UseGridDragOptions {
  /**
   * Client point → day column and minute, or null if the grid cannot
   * be measured at all. Implementations are expected to CLAMP rather
   * than reject: a release below the card still belongs to the last
   * minute of the column it is under, not to nowhere.
   */
  resolve: (clientX: number, clientY: number) => GridPoint | null;
  /** Called once per frame while dragging, and with null the moment
   *  there is nothing to show. Write to the DOM here rather than to
   *  state: this runs at the refresh rate. */
  onPreview: (preview: DragPreview | null) => void;
  /** The drag finished past the threshold. */
  onCommit: (commit: DragCommit) => void;
  /** Called at the top of every frame so the view can scroll itself
   *  when the pointer is near an edge. Runs BEFORE `resolve`, so the
   *  ghost lands correctly in the frame the view scrolled. */
  autoScroll?: (clientY: number) => void;
}

interface Session {
  kind: DragKind;
  pointerId: number;
  target: HTMLElement;
  fromX: number;
  fromY: number;
  /** The point the press landed on. */
  from: GridPoint;
  /** For move and resize. */
  origin?: Span;
  /** For a create: the date of the column the press landed in. */
  day?: Date;
  id?: string;
  armed: boolean;
  lastX: number;
  lastY: number;
  frame: number;
}

export function useGridDrag({ resolve, onPreview, onCommit, autoScroll }: UseGridDragOptions) {
  // The handlers below are made once; this keeps them looking at the
  // current render's callbacks instead of the first one's. Assigned in
  // a layout effect and not during render: a render that gets thrown
  // away — a transition, a Suspense retry — must not install its
  // callbacks over the ones that are on screen.
  const live = useRef({ resolve, onPreview, onCommit, autoScroll });
  useLayoutEffect(() => {
    live.current = { resolve, onPreview, onCommit, autoScroll };
  });

  const session = useRef<Session | null>(null);
  /** Set on release so the click that follows can be swallowed. */
  const dragged = useRef(false);
  const [active, setActive] = useState<{ kind: DragKind; id?: string } | null>(null);

  const span = useCallback((s: Session, at: GridPoint): Span | null => {
    if (s.kind === 'create') {
      if (!s.day) return null;
      // The column the press landed in, not the one the pointer has
      // wandered into: a new event is drawn down a day, not across.
      return resolveCreate(s.day, s.from.minutes, at.minutes);
    }
    if (!s.origin) return null;
    const deltaMinutes = at.minutes - s.from.minutes;
    if (s.kind === 'move') {
      // The column delta comes from the POINTER, not from the event's
      // own start day, which is what makes grabbing the second half of
      // an event that crosses midnight move the half you grabbed.
      return resolveMove(s.origin, deltaMinutes, at.dayIndex - s.from.dayIndex);
    }
    return resolveResize(s.origin, s.kind === 'resize-start' ? 'start' : 'end', deltaMinutes);
  }, []);

  /** Everything that has to happen however a drag ends. */
  const stop = useCallback(() => {
    const s = session.current;
    if (!s) return;
    // Cleared FIRST, so the `lostpointercapture` that releasing fires
    // cannot re-enter this and release a second time.
    session.current = null;
    cancelAnimationFrame(s.frame);
    window.removeEventListener('pointermove', onWindowMove);
    window.removeEventListener('pointerup', onWindowUp);
    window.removeEventListener('pointercancel', onWindowCancel);
    try {
      s.target.releasePointerCapture(s.pointerId);
    } catch {
      // Already released — the element may have unmounted under us,
      // which is the case this whole arrangement exists for.
    }
    setActive(null);
    live.current.onPreview(null);
    // `pointerup`, `mouseup` and `click` are dispatched in one task,
    // so a macrotask scheduled here runs after the click that
    // `swallowClick` exists to eat. Unconditionally, and from here
    // rather than from the release path, because the click does not
    // always arrive — a cancel, a release outside the window, an
    // element unmounted mid-drag — and a flag left standing would eat
    // somebody's next genuine tap on a chip that cannot be dragged.
    setTimeout(() => {
      dragged.current = false;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tick = useCallback(() => {
    const s = session.current;
    if (!s) return;
    s.frame = requestAnimationFrame(tick);
    if (!s.armed) return;
    live.current.autoScroll?.(s.lastY);
    const at = live.current.resolve(s.lastX, s.lastY);
    if (!at) {
      // The grid cannot be measured — mid-layout, or collapsed. Hide
      // the ghost rather than leaving it frozen somewhere stale.
      live.current.onPreview(null);
      return;
    }
    const next = span(s, at);
    if (!next) return;
    live.current.onPreview({
      ...next,
      kind: s.kind,
      dayIndex: s.kind === 'create' ? s.from.dayIndex : at.dayIndex,
    });
  }, [span]);

  const onWindowMove = useCallback((event: PointerEvent) => {
    const s = session.current;
    if (!s || event.pointerId !== s.pointerId) return;
    s.lastX = event.clientX;
    s.lastY = event.clientY;
    if (s.armed) return;
    if (Math.hypot(event.clientX - s.fromX, event.clientY - s.fromY) < THRESHOLD_PX) return;
    s.armed = true;
    dragged.current = true;
    setActive({ kind: s.kind, id: s.id });
  }, []);

  const onWindowUp = useCallback(
    (event: PointerEvent) => {
      const s = session.current;
      if (!s || event.pointerId !== s.pointerId) return;
      // The release has coordinates of its own, and they can be
      // further along than the last move reported. Commit from where
      // the pointer actually left off, not from a frame behind it.
      s.lastX = event.clientX;
      s.lastY = event.clientY;
      const armed = s.armed;
      const at = armed ? live.current.resolve(s.lastX, s.lastY) : null;
      const next = at ? span(s, at) : null;
      const kind = s.kind;
      const id = s.id;
      stop();
      if (armed && next) live.current.onCommit({ ...next, kind, id });
    },
    [span, stop],
  );

  const onWindowCancel = useCallback((event: PointerEvent) => {
    const s = session.current;
    // The pointerId check matters: on a hybrid machine a finger
    // touching the screen during a mouse drag fires a cancel for the
    // TOUCH pointer, and without this it would abandon the drag.
    if (!s || event.pointerId !== s.pointerId) return;
    stop();
  }, [stop]);

  const begin = useCallback(
    (
      event: React.PointerEvent,
      kind: DragKind,
      what: { id?: string; origin?: Span; day?: Date },
    ) => {
      if (event.button !== 0 || event.pointerType === 'touch') return;
      // A press while one is already live: end the old one rather than
      // leaving two sessions fighting over the same ghost.
      if (session.current) stop();
      const at = live.current.resolve(event.clientX, event.clientY);
      if (!at) return;
      const target = event.currentTarget as HTMLElement;
      // NOT preventDefault. Cancelling `pointerdown` is the usual way
      // to stop a press selecting text, but it also suppresses the
      // compatibility mouse events, and the chips rely on `click` for
      // keyboard activation. The view marks draggable things
      // `select-none` and `draggable={false}` instead, which buys the
      // same thing without touching activation at all.
      event.stopPropagation();
      try {
        target.setPointerCapture(event.pointerId);
      } catch {
        // No capture, no predictable trailing click. Don't start.
        return;
      }
      dragged.current = false;
      window.addEventListener('pointermove', onWindowMove);
      window.addEventListener('pointerup', onWindowUp);
      window.addEventListener('pointercancel', onWindowCancel);
      session.current = {
        kind,
        pointerId: event.pointerId,
        target,
        fromX: event.clientX,
        fromY: event.clientY,
        from: at,
        origin: what.origin,
        day: what.day,
        id: what.id,
        armed: false,
        lastX: event.clientX,
        lastY: event.clientY,
        frame: requestAnimationFrame(tick),
      };
    },
    [onWindowCancel, onWindowMove, onWindowUp, stop, tick],
  );

  // A drag left running because the grid went away would hold a frame
  // loop and three window listeners for the life of the tab.
  useEffect(() => stop, [stop]);

  /**
   * True for the one click that follows a drag, and consumed by
   * asking. The browser fires a click after any press-release on the
   * same element however far it travelled, and that click would open
   * the event you just moved.
   */
  const swallowClick = useCallback(() => {
    if (!dragged.current) return false;
    dragged.current = false;
    return true;
  }, []);

  return { begin, active, swallowClick };
}

export default useGridDrag;
